/**
 * 加密策略工厂
 */

import { AESCBCHMACStrategy } from './aes-cbc-hmac';
import { generateTimestamp, validateTimestamp } from './replay-guard';
import type {
  AsyncEncryptionStrategy,
  EncryptedRequest,
  EncryptionConfig,
} from './types';

/**
 * 创建加密策略实例
 *
 * @param config - 加密配置
 * @returns AES-256-CBC + HMAC-SHA256 异步加密策略实例
 *
 * @example
 * ```ts
 * const crypto = createEncryption({
 *   algorithm: 'aes-cbc-hmac',
 *   keys: {
 *     requestKeyHex: '64字符Hex请求密钥',
 *     responseKeyHex: '64字符Hex响应密钥',
 *   },
 * });
 *
 * const encrypted = await crypto.encrypt(data, Date.now());
 * ```
 */
export function createEncryption(
  config: EncryptionConfig,
): AsyncEncryptionStrategy {
  if (config.algorithm !== 'aes-cbc-hmac') {
    throw new Error(
      `[Crypto] Unsupported algorithm: ${config.algorithm}. Only 'aes-cbc-hmac' is supported.`,
    );
  }
  return new AESCBCHMACStrategy(config.keys);
}

/**
 * 加密请求数据的便捷函数
 *
 * 将数据加密并生成 HMAC 签名。
 *
 * @param strategy - 加密策略实例
 * @param data - 要加密的请求数据
 * @returns 加密结果 (密文 + 时间戳 + 签名)
 *
 * @example
 * ```ts
 * const crypto = createEncryption({ algorithm: 'aes-cbc-hmac', keys: { ... } });
 * const { encrypted, timestamp, signature } = await encryptRequestData(crypto, requestBody);
 *
 * headers['X-Encrypted'] = 'true';
 * headers['X-Timestamp'] = String(timestamp);
 * headers['X-Signature'] = signature;
 * body = encrypted;
 * ```
 */
export async function encryptRequestData(
  strategy: AsyncEncryptionStrategy,
  data: unknown,
): Promise<EncryptedRequest> {
  const timestamp = generateTimestamp();
  const encrypted = await strategy.encrypt(data, timestamp);

  let signature: string | undefined;
  if (strategy instanceof AESCBCHMACStrategy) {
    signature = await strategy.generateRequestSignature(encrypted, timestamp);
  }

  return { encrypted, timestamp, signature };
}

/**
 * 解密响应数据的便捷函数
 *
 * 验证时间戳和签名后解密数据。
 *
 * @param strategy - 加密策略实例
 * @param ciphertext - 密文
 * @param timestamp - 时间戳
 * @param options - 验证选项
 * @returns 解密后的数据
 * @throws 时间戳过期或签名验证失败时抛出错误
 */
export async function decryptResponseData(
  strategy: AsyncEncryptionStrategy,
  ciphertext: string,
  timestamp?: number,
  options?: { replayWindow?: number; forwardSkew?: number; signature?: string },
): Promise<unknown> {
  if (timestamp === undefined || timestamp === 0) {
    throw new Error(
      '[Crypto] Missing response timestamp, cannot verify replay protection',
    );
  }

  const isValid = validateTimestamp(timestamp, {
    window: options?.replayWindow,
    forwardSkew: options?.forwardSkew,
  });
  if (!isValid) {
    throw new Error(
      '[Crypto] Response timestamp expired, possible replay attack',
    );
  }

  if (strategy instanceof AESCBCHMACStrategy) {
    if (!options?.signature) {
      throw new Error(
        '[Crypto] Missing response signature, cannot verify response integrity',
      );
    }

    const isSignatureValid = await strategy.verifyResponseSignature(
      ciphertext,
      options.signature,
      timestamp,
    );
    if (!isSignatureValid) {
      throw new Error(
        '[Crypto] Invalid response signature, possible tampering detected',
      );
    }
  }

  return strategy.decrypt(ciphertext, timestamp);
}
